import * as React from "react";
import { useEffect, useState } from "react";
import { AppName } from "~shared/messageTypes";
import { CoopPoker } from "~lib/CoopPoker/index";
import { Lobby } from "~lib/Lobby/index";
import {
  PlayerJoinStateName,
  RoomInfo,
  UserInfo,
} from "~shared/namespace/Base";
import {
  ConnectionState,
  useConnectionState,
  useRoomInfo,
  useUserInfo,
} from "../App";

export interface AppIndex {
  hidden?: boolean;
  id: AppName;
  name: string;
  app: AppDefn<any>;
}

export interface CommonHooks {
  roomInfo?: RoomInfo;
  connectionState?: ConnectionState;
  player?: UserInfo;
  time: number;
}

export interface AppDefn<HookFnReturn> {
  app(arg: HookFnReturn & CommonHooks): React.ReactNode;
  hooks(active: boolean, commonHooks: CommonHooks): HookFnReturn;
}

export const ALL_APPS: AppIndex[] = [Lobby, CoopPoker];

export const GameNotImplemented = () => {
  return <>This game isn't ready to play yet!</>;
};

export const isInGameState = (state: PlayerJoinStateName | undefined) => {
  return (
    state === PlayerJoinStateName.IN_GAME ||
    state === PlayerJoinStateName.LEAVING
  );
};

export const useInterval = (
  interval: number,
  callback: () => void = () => {}
) => {
  useEffect(() => {
    const handler = setInterval(callback, interval);
    return () => clearInterval(handler);
  }, [callback, interval]);
};
export const useTime = (enabled: boolean, interval: number) => {
  const [time, setTime] = useState<number>(Date.now());
  useInterval(interval, () => {
    if (!enabled) return;
    setTime(Date.now());
  });
  return time;
};

export const usePlayerInThisGame = (game: AppName) => {
  const player = useUserInfo();
  const room = useRoomInfo();
  if (!player || !room) return false;
  const isInGame = isInGameState(
    room.players.find((x) => x.id === player.id)?.joinState
  );
  const isGameThis = room.currentGame === game;
  return isInGame && isGameThis;
};

export const App = (props: { app: AppName }) => {
  const { app } = props;

  const common: CommonHooks = {
    roomInfo: useRoomInfo(),
    player: useUserInfo(),
    time: useTime(true, 1000),
    connectionState: useConnectionState(),
  };

  return ALL_APPS.map((x) => {
    const appArgs = x.app.hooks(x.id === app, common);
    return x.id === app && x.app.app({ ...appArgs, ...common });
  }).find((x) => x);
};
